/**
 * Brush/Erase paint logic (engine 46.3). Brush and Erase are the same
 * traversal writing a different tile id (Erase writes EMPTY_TILE). Both:
 *   - step between pointer samples with Bresenham so a fast drag leaves no
 *     gaps ("line stepping"),
 *   - stamp an N×N block per cell (brush size), and
 *   - route every write through the active Stroke so undo is free.
 *
 * The functions here are pure over (LayerTiles, Stroke), so they unit-test
 * without a canvas.
 */

import { EMPTY_TILE, type TileID } from '../../model/constants';
import type { LayerTiles } from '../../model/chunk';
import type { Stroke } from '../../store/strokes';
import type { Stamp } from '../../model/types';

/** Integer cells on the line from (x0,y0) to (x1,y1), inclusive (Bresenham). */
export function bresenham(x0: number, y0: number, x1: number, y1: number): Array<[number, number]> {
    const cells: Array<[number, number]> = [];
    let dx = Math.abs(x1 - x0);
    let dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    let x = x0;
    let y = y0;
    // Safety bound: never loop forever on absurd inputs.
    for (let guard = 0; guard < 100000; guard++) {
        cells.push([x, y]);
        if (x === x1 && y === y1) break;
        const e2 = 2 * err;
        if (e2 >= dy) { err += dy; x += sx; }
        if (e2 <= dx) { err += dx; y += sy; }
    }
    return cells;
}

/**
 * Stamp an N×N block of `id` centered (floored) on the anchor cell, recording
 * each real change into the stroke. Returns true if anything changed.
 */
export function paintBlock(
    tiles: LayerTiles,
    stroke: Stroke,
    anchorX: number,
    anchorY: number,
    id: TileID,
    brushSize: number,
): boolean {
    const half = Math.floor(brushSize / 2);
    let changed = false;
    for (let oy = 0; oy < brushSize; oy++) {
        for (let ox = 0; ox < brushSize; ox++) {
            const cx = anchorX - half + ox;
            const cy = anchorY - half + oy;
            const before = tiles.getTile(cx, cy);
            if (before === id) continue;
            if (tiles.setTile(cx, cy, id)) {
                stroke.record(cx, cy, before, id);
                changed = true;
            }
        }
    }
    return changed;
}

/**
 * Paint from the previous sampled cell to the current one, stamping the block
 * along the Bresenham line. `prev` null = first sample (just the current cell).
 * Returns true if anything changed.
 */
export function paintStep(
    tiles: LayerTiles,
    stroke: Stroke,
    prev: { x: number; y: number } | null,
    curr: { x: number; y: number },
    id: TileID,
    brushSize: number,
): boolean {
    const path = prev ? bresenham(prev.x, prev.y, curr.x, curr.y) : [[curr.x, curr.y] as [number, number]];
    let changed = false;
    for (const [cx, cy] of path) {
        if (paintBlock(tiles, stroke, cx, cy, id, brushSize)) changed = true;
    }
    return changed;
}

/**
 * Stamp a multi-tile Stamp with its top-left corner at the anchor cell. Stamp
 * row 0 is the top row (as shown in the palette); the map is y-up, so row `r`
 * maps to world Y `anchorY - r`, keeping painted orientation identical to the
 * palette selection. Empty stamp entries are skipped (paint-through holes).
 * Returns true if anything changed.
 */
export function paintStamp(
    tiles: LayerTiles,
    stroke: Stroke,
    anchorX: number,
    anchorY: number,
    stamp: Stamp,
): boolean {
    let changed = false;
    for (let r = 0; r < stamp.h; r++) {
        for (let c = 0; c < stamp.w; c++) {
            const id = stamp.tiles[r * stamp.w + c];
            if (id === undefined || id === EMPTY_TILE) continue;
            const cx = anchorX + c;
            const cy = anchorY - r; // row 0 (top) → highest world Y
            const before = tiles.getTile(cx, cy);
            if (before === id) continue;
            if (tiles.setTile(cx, cy, id)) {
                stroke.record(cx, cy, before, id);
                changed = true;
            }
        }
    }
    return changed;
}

/**
 * Continuous stamp painting between two pointer samples. For a 1×1 stamp,
 * defers to the N×N block brush (so brush size works). For a larger stamp,
 * stamps the whole stamp at each Bresenham cell (Tiled-style overlap).
 */
export function paintStampStep(
    tiles: LayerTiles,
    stroke: Stroke,
    prev: { x: number; y: number } | null,
    curr: { x: number; y: number },
    stamp: Stamp,
    brushSize: number,
): boolean {
    const path = prev ? bresenham(prev.x, prev.y, curr.x, curr.y) : [[curr.x, curr.y] as [number, number]];
    const single = stamp.w === 1 && stamp.h === 1;
    let changed = false;
    for (const [cx, cy] of path) {
        if (single) {
            if (paintBlock(tiles, stroke, cx, cy, stamp.tiles[0] ?? EMPTY_TILE, brushSize)) changed = true;
        } else {
            if (paintStamp(tiles, stroke, cx, cy, stamp)) changed = true;
        }
    }
    return changed;
}
